import React, { useEffect, useState, FC, ChangeEvent } from "react"
import {
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
  Link,
} from "@material-ui/core"

import { useInsight } from "../../hooks/useInsight"
import { getIcon } from "../../utils/getInsightIcon"

import { colors } from "../../utils/theme"
import { HeaderHeight } from "../molecules/Header"
import { ComponentHeaderHeight } from "../../utils/constants"
import HowIcon from "../../assets/HowIcon.svg"
import ResourcesIcon from "../../assets/resources.svg"

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: HeaderHeight,
    backgroundColor: colors.neutral.white,
    paddingLeft: theme.spacing(2, 3),
    flex: 1,
    maxWidth: 1440,
  },
  contentRoot: {
    padding: theme.spacing(1, 4, 4),
  },
  title: {
    color: colors.neutral.denim,
    padding: theme.spacing(2, 3),
  },
  header: {
    height: ComponentHeaderHeight,
    maxWidth: 1440,
    margin: "0 auto",
  },
  navigation: {
    height: 92,
    width: "100%",
    display: "flex",
    alignItems: "center",
    padding: 0,
    margin: 0,
    border: 0,
    backgroundColor: "transparent",
    paddingLeft: theme.spacing(3),
    outline: "none",
  },
  tableHeader: {
    fontWeight: "bold",
    paddingLeft: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    color: colors.neutral.denim,
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${colors.neutral.clay}`,
  },
  chartContainer: {
    position: "relative",
    backgroundColor: "white",
    paddingTop: theme.spacing(3),
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 1,
    marginTop: "25px",
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    width: "100%",
  },
  content: {
    maxWidth: 1440,
    padding: theme.spacing(2, 3),
  },
  tableContent: {
    padding: theme.spacing(3),
    fontWeight: 400,
  },
  howIcon: {
    listStyleImage: `url(${HowIcon})`,
  },
  resourcesIcon: {
    listStyleImage: `url(${ResourcesIcon})`,
  },
}))

interface InsightTableProps {
  title: string
  children: React.ReactNode
}

export const InsightTable: FC<InsightTableProps> = ({ title, children }) => {
  const classes = useStyles()
  return (
    <div className={classes.chartContainer}>
      <div className={classes.headerContainer}>
        <Typography variant="h5" component="h5" className={classes.tableHeader}>
          {title}
        </Typography>
      </div>
      <div className={classes.tableContent}>{children}</div>
    </div>
  )
}
interface InsightDetailProps {
  insightPK: number
}

interface PlanArticles {
  content: string
  header_image?: string
  title?: string
}

export const InsightDetail: FC<InsightDetailProps> = ({ insightPK }) => {
  const classes = useStyles()
  const insight = useInsight(insightPK)
  const item = insight?.insight_recipes_by_pk
  const [planRadio, setPlanRadio] = useState("")
  const [planTexts, setPlanTexts] = useState<string[]>()
  const [planArticles, setPlanArticles] = useState<PlanArticles[] | undefined>()

  const handleRadio = (event: ChangeEvent<HTMLInputElement>) => {
    setPlanRadio((event.target as HTMLInputElement).value)
  }

  useEffect(() => {
    setPlanRadio(item?.insight_plans[0].responseText?.content ?? "")
  }, [item])

  useEffect(() => {
    const selectedPlan = item?.insight_plans.find(
      (plan) => plan.responseText?.content === planRadio
    )
    const getPlanTexts = selectedPlan?.insight_plan_texts.map(
      (plan) => plan.insight_text.content
    )
    const getPlanArticles: PlanArticles[] | undefined =
      selectedPlan?.insight_plan_articles.map((planArticle) => ({
        content: planArticle.resource.content ?? "",
        header_image: planArticle.resource.header_image ?? "",
        title: planArticle.resource.title ?? "",
      }))
    setPlanTexts(getPlanTexts)
    setPlanArticles(getPlanArticles)
  }, [planRadio, item])

  return (
    <>
      <div className={classes.title}>
        <Typography variant="h6" component="h6">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "20px",
            }}
          >
            <img
              src={getIcon(item?.insight_combo.feature)}
              alt="Logo"
              height={"25px"}
              style={{ marginRight: "5px" }}
            />
            {item?.insight_combo.display_text}
          </div>
        </Typography>
        <InsightTable title={"Observe"}>
          {item?.observeText?.content}
        </InsightTable>
        <InsightTable title={"Reflections"}>
          <>
            <Typography variant="subtitle1">
              {item?.reflectText?.content}
            </Typography>
            {item?.insight_reflects.map((reflect, i) => (
              <>
                <Typography>{reflect.reflectHeading?.content}</Typography>
                <ul>
                  {reflect.insight_reflect_texts.map((text) => (
                    <li>{text.insight_text.content}</li>
                  ))}
                </ul>
                {i < item.insight_reflects.length - 1 && (
                  <Typography variant="subtitle2">OR</Typography>
                )}
              </>
            ))}
          </>
        </InsightTable>
        <InsightTable title={item?.planText?.content ?? "Help Us Help You:"}>
          <RadioGroup value={planRadio} onChange={handleRadio}>
            {item?.insight_plans.map((plan) => (
              <FormControlLabel
                key={plan.responseText?.content}
                value={plan.responseText?.content}
                control={<Radio />}
                label={plan.responseText?.content}
              />
            ))}
          </RadioGroup>
        </InsightTable>
        <InsightTable title="Plans">
          <ul className={classes.howIcon}>
            {planTexts?.map((text) => (
              <li key={text}>
                <Typography>{text}</Typography>
              </li>
            ))}
          </ul>
        </InsightTable>
        <InsightTable title="Resources">
          <ul className={classes.resourcesIcon}>
            {planArticles?.map((article) => (
              <Link href={article.content}>
                <li key={article.content}>
                  <Typography>{article.title}</Typography>
                </li>
              </Link>
            ))}
          </ul>
        </InsightTable>
      </div>
    </>
  )
}

export default InsightDetail
